import React, { useEffect, useState } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { CreditCard, RefreshCw } from 'lucide-react';

const METHOD_BADGE = {
  stripe:  { label: 'Stripe',  bg: '#EFF6FF', color: '#1D4ED8' },
  paypal:  { label: 'PayPal',  bg: '#EEF2FF', color: '#4338CA' },
};

const STATUS_BADGE = {
  completed: { label: 'Paid',     bg: '#D1FAE5', color: '#065F46' },
  pending:   { label: 'Pending',  bg: '#FEF3C7', color: '#92400E' },
  refunded:  { label: 'Refunded', bg: '#F3F4F6', color: '#374151' },
  failed:    { label: 'Failed',   bg: '#FEE2E2', color: '#991B1B' },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtAmt = (amt, currency) => `${currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}${Number(amt || 0).toFixed(2)}`;

export default function MemberTransactions() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/payments/my/transactions')
      .then(r => setTxs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total = txs.filter(t => t.status === 'completed').reduce((s, t) => s + (t.amount || 0), 0);
  const refunded = txs.filter(t => t.status === 'refunded').reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <DashboardShell title="Transactions">
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total paid', value: `£${total.toFixed(2)}`, sub: `${txs.filter(t => t.status === 'completed').length} payment(s)` },
            { label: 'Refunded', value: `£${refunded.toFixed(2)}`, sub: `${txs.filter(t => t.status === 'refunded').length} refund(s)` },
            { label: 'Listing fees', value: txs.length, sub: 'total transactions' },
          ].map(c => (
            <div key={c.label} className="p-5 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
              <p className="text-2xl font-semibold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>{c.value}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{c.sub}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-subtle)' }} />)}</div>
          ) : txs.length === 0 ? (
            <div className="text-center py-16">
              <CreditCard size={32} className="mx-auto mb-3" style={{ color: 'var(--border-strong)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>No transactions yet</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Your payment history will appear here once you submit a listing.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="member-transactions-table">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-clr)', backgroundColor: 'var(--bg-subtle)' }}>
                  {['Date', 'Listing', 'Method', 'Amount', 'Status'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txs.map((tx, i) => {
                  const method = METHOD_BADGE[tx.payment_method] || { label: tx.payment_method, bg: '#F3F4F6', color: '#374151' };
                  const status = STATUS_BADGE[tx.status] || STATUS_BADGE.pending;
                  return (
                    <tr key={tx.id || i} style={{ borderBottom: '1px solid var(--border-clr)' }}>
                      <td className="px-5 py-4" style={{ color: 'var(--text-muted)' }}>{fmt(tx.created_at)}</td>
                      <td className="px-5 py-4 font-medium" style={{ color: 'var(--text)' }}>{tx.listing_title}</td>
                      <td className="px-5 py-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: method.bg, color: method.color }}>{method.label}</span>
                      </td>
                      <td className="px-5 py-4 font-semibold" style={{ color: 'var(--text)' }}>{fmtAmt(tx.amount, tx.currency)}</td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: status.bg, color: status.color }}>
                          {tx.status === 'refunded' && <RefreshCw size={10} />}
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
